import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { distinctUntilChanged, map, startWith } from 'rxjs';

import { PermissionKey } from '../auth/role-permissions.service';
import { TenantBootstrapService } from './tenant-bootstrap.service';
import { TenantConfig } from './tenant.model';

export type TenantModule =
  | 'public_site'
  | 'registration'
  | 'teams'
  | 'players'
  | 'schedule'
  | 'statistics'
  | 'standings'
  | 'playoffs'
  | 'payments'
  | 'payroll'
  | 'gyms'
  | 'social_graphics'
  | 'settings'
  | 'audit_logs';

const PUBLIC_LEAGUE_MODULES: TenantModule[] = [
  'schedule',
  'statistics',
  'standings',
  'playoffs',
];

const PERMISSION_MODULES: Partial<Record<PermissionKey, TenantModule>> = {
  'users.manage': 'players',
  'schedule.view': 'schedule',
  'schedule.create': 'schedule',
  'schedule.edit': 'schedule',
  'schedule.delete': 'schedule',
  'schedule.print': 'schedule',
  'schedule.manage_scores': 'statistics',
  'schedule.manage_staff': 'schedule',
  'schedule.edit_game_type': 'schedule',
  'seasons.manage': 'settings',
  'divisions.manage': 'settings',
  'teams.manage': 'teams',
  'gyms.manage': 'gyms',
  'tournaments.manage': 'settings',
  'tournaments.divisions.manage': 'settings',
  'tournaments.teams.manage': 'settings',
  'tournaments.schedule.manage': 'settings',
  'tournaments.playoff.manage': 'settings',
  'tournaments.gyms.manage': 'settings',
  'payments.manage': 'payments',
  'payments.repair_data': 'payments',
  'payroll.manage': 'payroll',
  'social.manage': 'social_graphics',
  'branding.manage': 'settings',
  'settings.manage': 'settings',
  'audit_logs.view': 'audit_logs',
};

function modulesForTenant(tenant: TenantConfig | null): Set<string> | null {
  if (!tenant) return null;
  return new Set((tenant.enabled_modules || []).map((moduleName) => moduleName.toLowerCase()));
}

function normalizeModules(moduleOrModules: TenantModule | TenantModule[] | null | undefined): TenantModule[] {
  if (!moduleOrModules) return [];
  return Array.isArray(moduleOrModules) ? moduleOrModules : [moduleOrModules];
}

@Injectable({ providedIn: 'root' })
export class TenantFeatureService {
  readonly enabledModules$ = this.tenantBootstrap.tenant$.pipe(
    startWith(this.tenantBootstrap.currentTenant),
    distinctUntilChanged(),
    map((tenant) => modulesForTenant(tenant))
  );

  constructor(private tenantBootstrap: TenantBootstrapService) {}

  isEnabled(moduleOrModules: TenantModule | TenantModule[] | null | undefined): boolean {
    const requiredModules = normalizeModules(moduleOrModules);
    if (requiredModules.length === 0) return true;

    const enabledModules = modulesForTenant(this.tenantBootstrap.currentTenant);
    if (!enabledModules) return false;

    return requiredModules.some((moduleName) => enabledModules.has(moduleName));
  }

  isPublicLeagueEnabled(): boolean {
    return this.isEnabled(PUBLIC_LEAGUE_MODULES);
  }

  moduleForPermission(permission: PermissionKey): TenantModule | null {
    return PERMISSION_MODULES[permission] || null;
  }

  isPermissionVisible(permission: PermissionKey): boolean {
    return this.isEnabled(this.moduleForPermission(permission));
  }
}

@Injectable({ providedIn: 'root' })
export class TenantFeatureGuard implements CanActivate {
  constructor(
    private tenantFeatures: TenantFeatureService,
    private router: Router
  ) {}

  canActivate(route: ActivatedRouteSnapshot): boolean | UrlTree {
    const moduleOrModules = route.data?.['tenantModule'] as TenantModule | TenantModule[] | undefined;
    return this.tenantFeatures.isEnabled(moduleOrModules)
      ? true
      : this.router.createUrlTree(['/']);
  }
}
